<template>
    <div v-if="loaded" class="nevs-content">
        <ModalAddCustomer :show="showAddCustomerModal" @done="addCustomerDone"></ModalAddCustomer>
        <div class="nevs-content-top-buttons">
            <NevsButton class="success help-save" @click="saveClick()"><i class="fa-solid fa-floppy-disk"></i>
                {{ $LANG.Get('buttons.save') }}
            </NevsButton>&nbsp;
            <NevsButton class="error help-back" @click="backClick"><i class="fa-solid fa-arrow-left"></i> {{
                    $LANG.Get('buttons.back')
                }}
            </NevsButton>
        </div>
        <NevsCard class="form form-float">
            <NevsSelect v-model="orderNote.supplier_id" :ajax="'select/customers'" :error="orderNoteValidation.supplier_id"
                        :label="$LANG.Get('fields.supplier')"></NevsSelect>
            <div v-if="this.selectedSupplier !== null" class="supplier-details">
                {{ this.selectedSupplier.address }}<br/>
                {{ this.selectedSupplier.postal_code }} {{ this.selectedSupplier.city }}<br/>
                {{ this.selectedSupplier.country }}<br/>
            </div>
            <NevsButton @click="showAddCustomerModal = true">
                {{ $LANG.Get('buttons.addNewSupplier') }}
            </NevsButton>
        </NevsCard>
        <NevsCard class="form form-float help-dates">
            <NevsDateField v-model="timeOfIssue.date" :error="timeOfIssueValidation.date"
                           :label="$LANG.Get('fields.dateOfIssue')"></NevsDateField>
            <NevsMaskedField v-model="timeOfIssue.time" :error="timeOfIssueValidation.time"
                             :label="$LANG.Get('fields.timeOfIssue')"
                             :mask="'NN:NN:NN'"></NevsMaskedField>
        </NevsCard>
        <NevsCard class="form form-float">
            <NevsSelect v-if="showCurrency" v-model="orderNote.currency" :ajax="currencyEndpoint"
                        :error="orderNoteValidation.currency"
                        :label="$LANG.Get('fields.currency')" :readonly="currencyReadonly"></NevsSelect>
            <NevsSelect v-if="(mode === 'add' || mode === 'copy') && sequences.length > 1"
                        v-model="orderNote.order_note_sequence_id"
                        :error="orderNoteValidation.order_note_sequence_id" :label="$LANG.Get('fields.sequence')"
                        :options="sequences"></NevsSelect>
            <NevsSelect v-model="orderNote.locale"
                        :error="orderNoteValidation.locale" :label="$LANG.Get('fields.language')"
                        :ajax="'select/languages'"></NevsSelect>
        </NevsCard>
        <NevsCard class="items-card">
            <NevsSelect v-model="selectedProduct" :ajax="'select/products'" :error="orderNoteValidation.items"
                        :label="$LANG.Get('labels.selectItem')" class="item-select help-item"></NevsSelect>
            <NevsButton class="details-button" @click="toggleDetails()"><i class="fa-solid fa-eye"></i> {{ $LANG.Get('buttons.details') }}</NevsButton>
            <div class="items-container">
                <table v-show="orderNote.items.length !== 0" class="items-table">
                    <thead>
                    <tr class="items-table-header">
                        <td v-for="(header, key) in headers" :key="key" :style="{'width': header.width}">{{
                                header.text
                            }}
                        </td>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item, key) in orderNote.items" :key="key">
                        <td class="items-table-buttons">
                            <NevsButton @click="removeItemClick(key)"><i class="fa-solid fa-trash"></i></NevsButton>
                        </td>
                        <td>
                            <NevsTextField v-model="item.description" :readonly="item.product_id!==null"></NevsTextField><br />
                            <NevsTextArea v-if="showDetails" v-model="item.details"></NevsTextArea>
                        </td>
                        <td>
                            <NevsSelect v-model="item.measurement_unit_id" :readonly="item.product_id!==null"
                                        :ajax="'select/measurement-units'"></NevsSelect>
                        </td>
                        <td>
                            <NevsNumberField v-model="item.price"></NevsNumberField>
                        </td>
                        <td>
                            <NevsNumberField v-model="item.quantity" :decimal-places="3"
                                             :thousand-separator="''"></NevsNumberField>
                        </td>
                        <td v-if="orderNote.tax_system">
                            <NevsNumberField v-model="item.tax"></NevsNumberField>
                        </td>
                        <td>
                            <NevsNumberField :readonly="true" v-model="item.total"></NevsNumberField>
                        </td>
                    </tr>
                    </tbody>
                </table>
                <table v-show="orderNote.items.length !== 0" class="summary-table">
                    <tbody>
                    <tr v-for="(base, key) in taxBases" :key="key">
                        <td>
                            {{ $LANG.Get('labels.taxBase') }} {{ base.base }} ({{ orderNote.currency }}):
                        </td>
                        <td>
                            <NevsNumberField v-model="base.value" :readonly="true"></NevsNumberField>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            {{ $LANG.Get('fields.total') }} ({{ orderNote.currency }}):
                        </td>
                        <td>
                            <NevsNumberField v-model="total" :readonly="true"></NevsNumberField>
                        </td>
                    </tr>
                    </tbody>
                </table>
                <div class="nevs-clear-float"></div>
            </div>
        </NevsCard>
        <NevsCard class="footer-container">
            <NevsTextField v-model="orderNote.payment_details" :label="$LANG.Get('fields.paymentDetails')"></NevsTextField>
            <NevsTextField v-model="orderNote.delivery_details" :label="$LANG.Get('fields.deliveryDetails')"></NevsTextField>
            <NevsTextArea v-model="orderNote.footer" :error="orderNoteValidation.footer"
                          :label="$LANG.Get('fields.footer')"></NevsTextArea>
            <NevsCheckbox v-model="orderNote.show_financial_data"
                           :label="$LANG.Get('fields.showFinancialData')"></NevsCheckbox>
        </NevsCard>
    </div>
</template>

<script>
import OrderNote from '@/models/OrderNote.js';
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import Big from 'big.js';
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsDateField from "@/components/nevs/NevsDateField.vue";
import NevsMaskedField from "@/components/nevs/NevsMaskedField.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";
import NevsTextArea from "@/components/nevs/NevsTextArea.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import ModalAddCustomer from "@/components/modals/ModalAddCustomer.vue";
import moment from "moment";

export default {
    name: "EntityOrderNote",
    components: {
        ModalAddCustomer,
        NevsCheckbox,
        NevsTextArea,
        NevsNumberField, NevsTextField, NevsMaskedField, NevsDateField, NevsSelect, NevsButton, NevsCard
    },
    data() {
        return {
            loaded: false,
            mode: this.$route.params.mode,
            orderNote: new OrderNote({
                id: this.$route.params.id
            }),
            orderNoteValidation: new OrderNote({}, true),
            selectedSupplier: null,
            selectedProduct: null,
            taxBases: [],
            tempTaxBases: [],
            total: 0,
            currencyReadonly: false,
            originalSupplierId: null,
            newSupplierData: null,
            supplierChanged: false,
            timeOfIssue: {
                date: null,
                time: null
            },
            timeOfIssueValidation: {
                date: '',
                time: ''
            },
            sequences: [],
            showCurrency: false,
            currencyEndpoint: 'select/currencies',
            showAddCustomerModal: false,
            showDetails: false
        }
    },
    computed: {
        headers() {
            let headers = [
                {
                    text: '',
                    sortable: false,
                    value: 'actions',
                    width: '50px',
                    class: 'pa-0'
                },
                {
                    text: this.$LANG.Get('fields.description'),
                    sortable: false,
                    value: 'description',
                    class: 'pa-0 description-width'
                },
                {
                    text: this.$LANG.Get('fields.measurementUnit'),
                    sortable: false,
                    value: 'measurement_unit',
                    class: 'pa-0',
                    width: "100px"
                },
                {
                    text: this.$LANG.Get('fields.price') + ' (' + this.orderNote.currency + ')',
                    sortable: false,
                    value: 'price',
                    class: 'pa-0',
                    width: "100px"
                },
                {
                    text: this.$LANG.Get('fields.quantity'),
                    sortable: false,
                    value: 'quantity',
                    class: 'pa-0',
                    width: "100px"
                },
                {
                    text: this.$LANG.Get('fields.total') + ' (' + this.orderNote.currency + ')',
                    sortable: false,
                    value: 'total',
                    class: 'pa-0',
                    width: "100px"
                }
            ]
            if (this.orderNote.tax_system) {
                headers.splice(5, 0, {
                    text: this.$LANG.Get('fields.tax'),
                    sortable: false,
                    value: 'tax',
                    class: 'pa-0',
                    width: "100px"
                })
            }
            return headers;
        }
    },
    watch: {
        'orderNote.locale': function () {
            if (this.loaded) {
                let vm = this;
                for (let i = 0; i < this.orderNote.items.length; i++) {
                    if (this.orderNote.items[i].product_id !== null) {
                        this.$API.APICall('get', 'products/' + this.orderNote.items[i].product_id, {}, (data, success) => {
                            if (success) {
                                let newName = (data.product['name_' + vm.orderNote.locale] !== undefined) ? data.product['name_' + vm.orderNote.locale] : data.product.name;
                                if (newName != '') vm.orderNote.items[i].description = newName;
                            }
                        }, false);
                    }
                }
                this.orderNote.footer = this.$store.state.user.client['order_note_footer_template_' + this.orderNote.locale];
            }
        },
        'orderNote.supplier_id': function () {
            if (this.orderNote.supplier_id != null) {
                let vm = this;
                this.$API.APICall('get', 'customers/' + this.orderNote.supplier_id, {}, (data, success) => {
                    if (success) {
                        vm.selectedSupplier = data.customer;
                    }
                });
            } else {
                this.selectedSupplier = null;
            }
        },
        'selectedProduct': function () {
            if (this.selectedProduct != null) {
                this.addItem();
            }
        },
        'orderNote.items': {
            handler: function () {
                this.calculateTotals();
            },
            deep: true
        },
        'orderNote.currency': {
            handler: function () {
                this.loadCurrencyRate();
            },
            deep: true
        },
        'timeOfIssue': {
            handler: function () {
                this.showCurrency = false;
                this.currencyEndpoint = 'select/currencies';
                if (this.timeOfIssue.date + ' ' + this.timeOfIssue.time < this.$store.state.settings.EUR_TRANSITION) {
                    this.currencyEndpoint = 'select/currencies-hrk';
                }
                this.$nextTick(() => {
                    this.orderNote.time_of_issue = this.timeOfIssue.date + ' ' + this.timeOfIssue.time;
                    this.showCurrency = true;
                });
            },
            deep: true
        },
        'orderNote.time_of_issue': {
            handler: function () {
                this.timeOfIssue.date = this.orderNote.time_of_issue.split(' ')[0];
                this.timeOfIssue.time = this.orderNote.time_of_issue.split(' ')[1];
                if (this.orderNote.time_of_issue > moment().format('YYYY-MM-DD') + ' 23:59:59') {
                    this.orderNote.currency = this.$HELPERS.GetDefaultCurrency();
                    this.currencyReadonly = true;
                    this.loadCurrencyRate();
                } else {
                    this.currencyReadonly = false;
                    this.loadCurrencyRate();
                }
            },
            deep: true
        }
    },
    methods: {
        addCustomerDone(customerId) {
            this.showAddCustomerModal = false;
            if (customerId) {
                this.orderNote.supplier_id = customerId;
            }
        },
        toggleDetails() {
            this.showDetails = !this.showDetails;
        },
        loadCurrencyRate() {
            let vm = this;
            let oldRate = this.orderNote.currency_rate;
            let loaded = this.loaded;
            this.$API.APICall('get', 'currencies/' + this.orderNote.currency, {date: this.orderNote.time_of_issue}, (data, success) => {
                if (success) {
                    if (data.rate != null) {
                        vm.orderNote.currency_rate = data.rate;
                        if (loaded) {
                            for (let index in vm.orderNote.items) {
                                let item = vm.orderNote.items[index];
                                let price = Big(item.price);
                                vm.orderNote.items[index]['price'] = ((price * oldRate).toFixed(2) / Big(data.rate)).toFixed(2);
                            }
                        }
                    }
                }
            }, false);
        },
        calculateTotals() {
            this.tempTaxBases = [];
            this.total = Big(0);
            for (let index in this.orderNote.items) {
                let item = this.orderNote.items[index];
                let total = Big(item.price).times(Big(item.quantity));
                total = Big(total.toFixed(2));
                if (this.orderNote.tax_system) {
                    this.addToTempBase(item.tax, total);
                }
                this.orderNote.items[index]['total'] = total.toFixed(2);
                this.total = this.total.add(total);
            }
            this.taxBases = JSON.parse(JSON.stringify(this.tempTaxBases));
            if (this.orderNote.tax_system) {
                this.total = Big(0);
                for (let index in this.taxBases) {
                    let currentBase = this.taxBases[index];
                    let currentTaxAmount = (Big(currentBase.value).times(Big(currentBase.base)).div(Big(100))).toFixed(2);
                    this.total = this.total.add(currentTaxAmount).add(Big(currentBase.value));
                }
            }
        },
        addToTempBase(base, value) {
            let term = false;
            for (let index in this.tempTaxBases) {
                let currentBase = this.tempTaxBases[index];
                if (currentBase.base === base) {
                    term = true;
                    currentBase.value = Big(currentBase.value).add(Big(value)).toString();
                }
            }
            if (!term) {
                this.tempTaxBases.push({
                    base: base,
                    value: Big(value).toString()
                });
            }
        },
        backClick() {
            if (window.history.length === 1) {
                window.close();
            } else {
                this.$router.back();
            }
        },
        saveClick() {
            if (this.orderNote.supplier_id !== this.originalSupplierId) {
                this.saveAction();
            } else {
                if (!this.supplierChanged) {
                    this.saveAction();
                } else {
                    let vm = this;
                    this.$LOCAL_BUS.TriggerEvent('popup', {
                        type: 'confirm',
                        text: this.$LANG.Get('alerts.supplierChangesQuestionOrderNote'),
                        callback: (response) => {
                            if (response) {
                                vm.orderNote.supplier_data = JSON.parse(JSON.stringify(vm.newSupplierData));
                                vm.supplierChanged = false;
                            }
                            vm.saveAction();
                        }
                    });
                }
            }
        },
        saveAction() {
            let success = true;

            if (this.orderNote.supplier_id == null) {
                success = false;
                this.orderNoteValidation.supplier_id = this.$LANG.Get('labels.supplierIdMissing');
            } else {
                this.orderNoteValidation.supplier_id = '';
            }

            if ((this.orderNote.time_of_issue == null) || (!this.$HELPERS.DateTimePattern().test(this.orderNote.time_of_issue))) {
                success = false;
                this.timeOfIssueValidation.time = this.$LANG.Get('labels.timeOfIssueMissing');
            } else {
                this.timeOfIssueValidation.time = '';
            }

            let itemsValidation = '';
            if (this.orderNote.items.length === 0) {
                success = false;
                itemsValidation = this.$LANG.Get('labels.noItems');
            }
            let termDescription = false;
            let termNegative = false;
            for (let item of this.orderNote.items) {
                if (item.description === '') {
                    termDescription = true;
                    break;
                }
                if (item.quantity < 0 || item.price < 0) {
                    termNegative = true;
                    break;
                }
            }
            if (termDescription) {
                if (itemsValidation !== '') {
                    itemsValidation += '\n';
                }
                success = false;
                itemsValidation += this.$LANG.Get('labels.emptyItem');
            }
            if (termNegative) {
                if (itemsValidation !== '') {
                    itemsValidation += '\n';
                }
                success = false;
                itemsValidation += this.$LANG.Get('labels.negativeItem');
            }
            this.orderNoteValidation.items = itemsValidation;

            if (success) {
                let data = JSON.parse(JSON.stringify(this.orderNote));
                let vm = this;
                let action = 'post';
                let addition = '';
                if (this.mode === 'edit') {
                    action = 'put';
                    addition = '/' + this.orderNote.id;
                }
                this.$API.APICall(action, 'order-notes' + addition, data, (data, success) => {
                    if (success) {
                        vm.$CROSS_TAB_BUS.TriggerEvent('reload-order-notes', {});
                        if (action === 'post') {
                            vm.$LOCAL_BUS.TriggerEvent('notification', {text: this.$LANG.Get('alerts.recordSaved')});
                        } else {
                            vm.$LOCAL_BUS.TriggerEvent('notification', {text: this.$LANG.Get('alerts.recordSaved')});
                        }
                        if (vm.mode === 'add' || vm.mode === 'copy') {
                            vm.backClick();
                        }
                    } else {
                        if (data.error === 'invalid sequence later') {
                            vm.$LOCAL_BUS.TriggerEvent('popup', {
                                text: vm.$LANG.Get('alerts.orderNoteSequenceLater'),
                                type: 'alert'
                            });
                        } else if (data.error === 'invalid sequence earlier') {
                            vm.$LOCAL_BUS.TriggerEvent('popup', {
                                text: vm.$LANG.Get('alerts.orderNoteSequenceEarlier'),
                                type: 'alert'
                            });
                        } else if (data.error === 'invalid year') {
                            vm.$LOCAL_BUS.TriggerEvent('popup', {
                                text: vm.$LANG.Get('alerts.orderNoteInvalidYear'),
                                type: 'alert'
                            });
                        } else {
                            vm.$HELPERS.HandleServerError(vm, data);
                        }
                    }
                });
            }
        },
        addItem() {
            if (this.selectedProduct === null) return;
            if (this.selectedProduct === -1) {
                let newItem = {
                    description: '',
                    details: '',
                    price: 0,
                    quantity: 1,
                    tax: this.$store.state.settings.DEFAULT_TAX,
                    measurement_unit_id: (this.$store.state.user.client.default_measurement_unit_id !== null) ? this.$store.state.user.client.default_measurement_unit_id : 1,
                    total: 0,
                    product_id: null
                }
                if (!this.$store.state.user.client.tax_system) newItem.tax = 0;
                this.orderNote.items.push(newItem);
                this.$nextTick(() => {
                    this.selectedProduct = null;
                });
            } else {
                let vm = this;
                this.$API.APICall('get', 'products/' + this.selectedProduct, {}, (data, success) => {
                    if (success) {
                        let newItem = {
                            description: (data.product['name_' + vm.orderNote.locale] !== undefined) ? data.product['name_' + vm.orderNote.locale] : data.product.name,
                            details: '',
                            price: data.product.price * this.orderNote.currency_rate,
                            quantity: 1,
                            tax: data.product.tax,
                            measurement_unit_id: data.product.measurement_unit_id,
                            total: 0,
                            product_id: data.product.id
                        }
                        if (this.orderNote.time_of_issue < this.$store.state.settings.EUR_TRANSITION) {
                            newItem.price = data.product.price / this.orderNote.currency_rate;
                        }
                        vm.orderNote.items.push(newItem);
                        vm.selectedProduct = null;
                    } else {
                        vm.$HELPERS.HandleServerError(vm, data);
                    }
                }, false);
            }
        },
        removeItemClick(index) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                    if (response) {
                        vm.orderNote.items.splice(index, 1);
                    }
                }
            });
        }
    },
    mounted() {
        if (!this.$store.state.user.client.plus) {
            this.$LOCAL_BUS.TriggerEvent('popup', {text: this.$LANG.Get('alerts.unauthorized'), type: 'alert'});
            this.$router.push('/');
            return;
        }
        this.$store.commit('setHelpOverlay', [
            {
                class: 'help-save',
                html: this.$LANG.Get('help.save'),
                left: -100,
                top: 0,
                width: 150
            },
            {
                class: 'help-back',
                html: this.$LANG.Get('help.back'),
                left: 85,
                top: -30,
                width: 300
            },
            {
                class: 'help-dates',
                html: this.$LANG.Get('help.dates'),
                left: 0,
                top: 0,
                width: 300
            },
            {
                class: 'help-item',
                html: this.$LANG.Get('help.item'),
                left: 300,
                top: -30,
                width: 300
            }
        ]);

        this.$store.commit('selectMenu', 'orderNotes');
        this.$store.commit('selectSubMenu', null);

        this.sequences = this.$store.state.user.order_note_sequences;
        if (this.sequences.length > 0) {
            this.orderNote.order_note_sequence_id = this.$store.state.user.default_order_note_sequence_id;
        } else {
            this.$LOCAL_BUS.TriggerEvent('popup', {text: this.$LANG.Get('alerts.unauthorized'), type: 'alert'});
            this.$router.push('/');
        }

        if (this.mode === 'add') {
            this.$store.commit('setBreadcrumbs', [
                {
                    label: this.$LANG.Get('modules.orderNotes'),
                    link: '/order-notes'
                },
                {
                    label: this.$LANG.Get('modules.newOrderNote'),
                    link: null
                }
            ]);

            this.orderNote.tax_system = this.$store.state.user.client.tax_system;
            this.orderNote.time_of_issue = moment().format('YYYY-MM-DD HH:mm:ss');
            this.orderNote.footer = this.$store.state.user.client['order_note_footer_template_' + this.$store.state.locale];
            this.orderNote.locale = this.$store.state.locale;

            this.$nextTick(() => {
                this.loaded = true;
            });
        } else if (this.mode === 'edit') {
            let vm = this;
            this.$API.APICall('get', 'order-notes/' + this.orderNote.id, {}, (data, success) => {
                if (success) {
                    if (data.order_note !== null) {
                        vm.orderNote.Fill(data.order_note);
                        vm.showDetails = vm.orderNote.items.some(item => item.details && item.details !== '');
                        vm.supplierChanged = data.order_note.supplier_changed;
                        vm.originalSupplierId = data.order_note.supplier_id;
                        vm.newSupplierData = data.order_note.new_supplier_data;
                        vm.$store.commit('setBreadcrumbs', [
                            {
                                label: vm.$LANG.Get('modules.orderNotes'),
                                link: '/order-notes'
                            },
                            {
                                label: vm.orderNote.number,
                                link: null
                            }
                        ]);
                    } else {
                        vm.$HELPERS.HandleServerError(vm, data);
                    }
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
                vm.$nextTick(() => {
                    vm.loaded = true;
                });
            });
        } else if (this.mode === 'copy') {
            let vm = this;
            this.$API.APICall('get', 'order-notes/' + this.orderNote.id, {}, (data, success) => {
                if (success) {
                    vm.orderNote.id = 0;
                    vm.orderNote.time_of_issue = moment().format('YYYY-MM-DD HH:mm:ss');
                    vm.orderNote.supplier_id = data.order_note.supplier_id;
                    vm.orderNote.currency = data.order_note.currency;
                    if (vm.orderNote.time_of_issue >= this.$store.state.settings.EUR_TRANSITION && vm.orderNote.currency === 'HRK') {
                        vm.orderNote.currency = 'EUR';
                    }
                    let items = JSON.parse(JSON.stringify(data.order_note.items));
                    for (let i = 0; i < items.length; i++) {
                        items[i].id = undefined;
                    }
                    vm.orderNote.items = items;
                    vm.showDetails = vm.orderNote.items.some(item => item.details && item.details !== '');
                    vm.orderNote.payment_details = data.order_note.payment_details;
                    vm.orderNote.delivery_details = data.order_note.delivery_details;
                    vm.orderNote.footer = data.order_note.footer;
                    vm.orderNote.tax_system = vm.$store.state.user.client.tax_system;
                    vm.orderNote.locale = data.order_note.locale;

                    this.$store.commit('setBreadcrumbs', [
                        {
                            label: this.$LANG.Get('modules.orderNotes'),
                            link: '/order-notes'
                        },
                        {
                            label: this.$LANG.Get('modules.newOrderNote'),
                            link: null
                        }
                    ]);
                    vm.$nextTick(() => {
                        vm.loaded = true;
                    });
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    }
}
</script>

<style scoped>
.form {
    max-width: 300px;
    margin-bottom: 10px;
}

.form-float {
    width: 300px;
    float: left;
    margin-right: 10px;
    margin-bottom: 10px;
    padding: 15px;
}

.form-float h2 {
    margin: 0 0 5px;
}

.supplier-details {
    margin: 5px;
}

.items-card {
    clear: both;
}

.details-button {
    float: right;
    margin-bottom: 5px;
}

.items-container {
    clear: both;
    overflow-x: auto;
    white-space: nowrap;
}

.footer-container {
    margin-top: 10px;
    width: calc(100% - 20px);
}

.item-select {
    max-width: 500px;
}

.items-table {
    width: 100%;
    min-width: 1000px;
}

.items-table-header td {
    font-size: 12px;
    padding-left: 5px;
}

.items-table td {
    vertical-align: top;
}

.items-table-buttons {
    text-align: center;
}

.items-table-buttons span {
    cursor: pointer;
}

/* Match the button box to the field input (same 13px vertical padding) and offset it by the
   field wrapper's 5px top padding, so the buttons line up with the first row of inputs. */
.items-table-buttons .nevs-button {
    padding: 13px;
    margin-top: 5px;
}

.summary-table {
    float: right;
    margin-top: 30px;
    width: 400px;
}

@media only screen and (max-width: 500px) {
    .summary-table {
        float: none;
    }
}

</style>